import type { MeetingSessionType } from '../types/championship.js';
import { sessionTypeLabel } from '../types/championship.js';
import {
  COURSE_CURRENCY_FEWEST_DICE_ALL_QUALS,
  scoreCourseCurrencyQualHeatWin,
  scoreCourseCurrencySessionPosition,
  scoreOvertakeDuels,
  scoreQualRace,
  scoreSessionPosition,
} from './meetingPoints.js';
import { sessionResultOvertakeDuelsForScoring } from './driverPalmares.js';
import {
  buildQualStandings,
  sortQualStandings,
  type QualHeatResult,
  type QualTiebreakResult,
} from './meetingQualification.js';

export type SeasonPointBucket = 'championship' | 'qualification' | 'course';

export type SeasonPointEventKind =
  | 'session_position'
  | 'overtake_duels'
  | 'qual_heat_win'
  | 'qual_fewest_dice'
  | 'qual_race_points'
  /** @deprecated Anciens kinds championnat — conservés pour lecture ledger legacy. */
  | 'qual_manche_bonus'
  | 'qual_championship';

/** Une ligne de gain (championnat, qualif ou monnaie course). */
export interface SeasonPointEvent {
  kind: SeasonPointEventKind;
  bucket: SeasonPointBucket;
  points: number;
  meetingNumber: number;
  meetingId?: number;
  sessionType?: string;
  detail: string;
  label: string;
  circuitName?: string;
  position?: number;
}

export interface SeasonPointBreakdown {
  driverId: number;
  total: number;
  events: SeasonPointEvent[];
}

export interface DriverSeasonPointsBreakdown {
  driverId: number;
  championship: SeasonPointBreakdown;
  qualification: SeasonPointBreakdown;
  course: SeasonPointBreakdown;
}

export interface SeasonPointPhaseBadge {
  code: string;
  label: string;
  title: string;
}

export function formatSeasonPointMeetingBadge(meetingNumber: number): string {
  return `M${meetingNumber}`;
}

export function formatSeasonPointMeetingLabel(meetingNumber: number): string {
  return `Meeting ${meetingNumber}`;
}

export function formatSeasonPointMeetingTitle(meetingNumber: number, circuitName?: string): string {
  const circuit = circuitName?.trim();
  if (circuit) return `Meeting ${meetingNumber} — ${circuit}`;
  return `Meeting ${meetingNumber}`;
}

export function parseOrdinalFr(detail: string): number | null {
  const trimmed = detail.trim();
  if (trimmed === '1er') return 1;
  const m = /^(\d+)e$/i.exec(trimmed);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function formatSeasonPointPhaseBadge(parts: {
  kind: SeasonPointEventKind;
  detail: string;
  sessionType?: string;
}): SeasonPointPhaseBadge | null {
  switch (parts.kind) {
    case 'qual_manche_bonus': {
      const manche = parts.detail.trim() || '?';
      return {
        code: `Q${manche}`,
        label: `Qualification manche ${manche}`,
        title: `Qualification — manche ${manche}`,
      };
    }
    case 'qual_championship':
      return {
        code: 'Q',
        label: 'Qualifications',
        title: 'Classement qualifications',
      };
    case 'qual_heat_win':
    case 'qual_race_points':
    case 'qual_fewest_dice':
      return {
        code: 'Q',
        label: 'Qualifications',
        title: 'Qualifications',
      };
    case 'session_position':
    case 'overtake_duels': {
      const session = parts.sessionType;
      if (session === 'semi_1') {
        return { code: 'DF1', label: 'Demi-finale A', title: 'Demi-finale A' };
      }
      if (session === 'semi_2') {
        return { code: 'DF2', label: 'Demi-finale B', title: 'Demi-finale B' };
      }
      if (session === 'final') {
        return { code: 'F', label: 'Finale', title: 'Finale' };
      }
      if (session?.startsWith('qual')) {
        return { code: 'Q', label: 'Qualifications', title: 'Qualifications' };
      }
      if (session) {
        const full = sessionTypeLabel(session as MeetingSessionType);
        return { code: 'S', label: full, title: full };
      }
      return null;
    }
    default:
      return null;
  }
}

export function compareSeasonPointEventsRecentFirst(
  a: SeasonPointEvent,
  b: SeasonPointEvent
): number {
  if (a.meetingNumber !== b.meetingNumber) {
    return b.meetingNumber - a.meetingNumber;
  }
  const phaseRank = (e: SeasonPointEvent): number => {
    switch (e.kind) {
      case 'session_position':
      case 'overtake_duels': {
        if (e.sessionType === 'final') return 400;
        if (e.sessionType === 'semi_2') return 300;
        if (e.sessionType === 'semi_1') return 200;
        return 150;
      }
      case 'qual_fewest_dice':
        return 120;
      case 'qual_championship':
        return 100;
      case 'qual_heat_win':
      case 'qual_race_points':
      case 'qual_manche_bonus':
        return 50;
      default:
        return 0;
    }
  };
  const ra = phaseRank(a);
  const rb = phaseRank(b);
  if (ra !== rb) return rb - ra;
  const kindTie = (k: SeasonPointEventKind): number => {
    if (k === 'session_position' || k === 'qual_race_points') return 0;
    if (k === 'overtake_duels' || k === 'qual_heat_win') return 1;
    return 2;
  };
  return kindTie(a.kind) - kindTie(b.kind);
}

export function formatSeasonPointEventBody(parts: {
  kind: SeasonPointEventKind;
  detail: string;
  sessionType?: string;
}): string {
  switch (parts.kind) {
    case 'qual_manche_bonus':
      return `Bonus manche ${parts.detail}`;
    case 'qual_championship':
      return `Place qualifs · ${parts.detail}`;
    case 'qual_heat_win':
      return `Victoire heat · ${parts.detail}`;
    case 'qual_fewest_dice':
      return `Moins de lancers · ${parts.detail}`;
    case 'qual_race_points':
      return `Points qualif · ${parts.detail}`;
    case 'session_position': {
      const session =
        parts.sessionType != null
          ? sessionTypeLabel(parts.sessionType as MeetingSessionType)
          : 'Course';
      return `Place · ${session} · ${parts.detail}`;
    }
    case 'overtake_duels': {
      const session =
        parts.sessionType != null
          ? sessionTypeLabel(parts.sessionType as MeetingSessionType)
          : 'Course';
      return `Dépassements · ${session} · ${parts.detail}`;
    }
    default:
      return parts.detail;
  }
}

export function formatSeasonPointTipBody(parts: {
  kind: SeasonPointEventKind;
  detail: string;
}): string {
  switch (parts.kind) {
    case 'qual_manche_bonus':
      return 'Bonus';
    case 'qual_heat_win':
      return 'Victoire heat';
    case 'qual_fewest_dice':
      return `Moins de lancers · ${parts.detail}`;
    case 'qual_race_points':
      return parts.detail;
    case 'overtake_duels':
      return `Dépassements · ${parts.detail}`;
    case 'qual_championship':
    case 'session_position':
      return '';
    default:
      return parts.detail;
  }
}

export function formatSeasonPointEventLabel(parts: {
  meetingNumber: number;
  kind: SeasonPointEventKind;
  detail: string;
  points: number;
  sessionType?: string;
}): string {
  const meeting = `Meeting ${parts.meetingNumber}`;
  const signed = parts.points > 0 ? `+${parts.points}` : `${parts.points}`;
  const body = formatSeasonPointEventBody(parts);
  return `${meeting} · ${body} (${signed})`;
}

export function ordinalFr(n: number): string {
  if (n === 1) return '1er';
  return `${n}e`;
}

export interface SeasonPointSessionInput {
  meetingId: number;
  meetingNumber: number;
  sessionType: string;
  status: string;
  circuitName?: string;
  results: readonly {
    driverId: number;
    position: number;
    overtakeDuels?: number;
    overtakesSucceeded?: number;
    overtakesCountered?: number;
    diceRolls?: number;
  }[];
}

export interface SeasonPointQualMeetingInput {
  meetingId: number;
  meetingNumber: number;
  circuitName?: string;
  qualHeatsByManche: ReadonlyMap<number, readonly QualHeatResult[]>;
  tiebreakResults: readonly QualTiebreakResult[];
  /** Qualifs du meeting terminées (pour bonus moins de lancers). */
  qualComplete: boolean;
}

/** Events championnat uniquement : places demi + finale. */
export function buildChampionshipPointEventsForDriver(
  driverId: number,
  sessions: readonly SeasonPointSessionInput[]
): SeasonPointEvent[] {
  const events: SeasonPointEvent[] = [];
  for (const sess of sessions) {
    if (sess.status !== 'completed') continue;
    if (sess.sessionType.startsWith('qual')) continue;
    for (const r of sess.results) {
      if (r.driverId !== driverId) continue;
      const sessionPoints = scoreSessionPosition(
        sess.sessionType as MeetingSessionType,
        r.position
      );
      if (sessionPoints <= 0) continue;
      const detail = ordinalFr(r.position);
      events.push({
        kind: 'session_position',
        bucket: 'championship',
        points: sessionPoints,
        meetingNumber: sess.meetingNumber,
        meetingId: sess.meetingId,
        sessionType: sess.sessionType,
        circuitName: sess.circuitName,
        position: r.position,
        detail,
        label: formatSeasonPointEventLabel({
          meetingNumber: sess.meetingNumber,
          kind: 'session_position',
          detail,
          points: sessionPoints,
          sessionType: sess.sessionType,
        }),
      });
    }
  }
  return events;
}

/** @deprecated Alias — préférer buildChampionshipPointEventsForDriver. */
export function buildSeasonPointEventsForDriver(
  driverId: number,
  _meetings: readonly SeasonPointQualMeetingInput[],
  sessions: readonly SeasonPointSessionInput[],
  _poolSize?: number
): SeasonPointEvent[] {
  return buildChampionshipPointEventsForDriver(driverId, sessions);
}

/** Points de qualification (heats) — hors classement championnat. */
export function buildQualificationPointEventsForDriver(
  driverId: number,
  meetings: readonly SeasonPointQualMeetingInput[]
): SeasonPointEvent[] {
  const events: SeasonPointEvent[] = [];
  for (const meeting of meetings) {
    const allHeats = [...meeting.qualHeatsByManche.values()].flat();
    if (allHeats.length === 0) continue;
    const standings = sortQualStandings(buildQualStandings([...allHeats]), meeting.tiebreakResults);
    const row = standings.find((s) => s.driverId === driverId);
    if (!row || row.qualPoints <= 0) continue;
    const rank = standings.findIndex((s) => s.driverId === driverId) + 1;
    const detail = `${ordinalFr(rank > 0 ? rank : 1)} · ${row.qualPoints} pts`;
    events.push({
      kind: 'qual_race_points',
      bucket: 'qualification',
      points: row.qualPoints,
      meetingNumber: meeting.meetingNumber,
      meetingId: meeting.meetingId,
      circuitName: meeting.circuitName,
      position: rank > 0 ? rank : undefined,
      detail,
      label: formatSeasonPointEventLabel({
        meetingNumber: meeting.meetingNumber,
        kind: 'qual_race_points',
        detail,
        points: row.qualPoints,
      }),
    });
  }
  return events;
}

/** Monnaie course selon Trello « Monnaie du jeu ». */
export function buildCourseCurrencyEventsForDriver(
  driverId: number,
  meetings: readonly SeasonPointQualMeetingInput[],
  sessions: readonly SeasonPointSessionInput[]
): SeasonPointEvent[] {
  const events: SeasonPointEvent[] = [];

  for (const meeting of meetings) {
    if (meeting.qualComplete) {
      const allHeats = [...meeting.qualHeatsByManche.values()].flat();
      if (allHeats.length > 0) {
        const totals = new Map<number, number>();
        for (const h of allHeats) {
          totals.set(h.driverId, (totals.get(h.driverId) ?? 0) + h.diceRolls);
        }
        const minTotal = Math.min(...totals.values());
        if ((totals.get(driverId) ?? Infinity) === minTotal) {
          const detail = `${minTotal} lancers`;
          events.push({
            kind: 'qual_fewest_dice',
            bucket: 'course',
            points: COURSE_CURRENCY_FEWEST_DICE_ALL_QUALS,
            meetingNumber: meeting.meetingNumber,
            meetingId: meeting.meetingId,
            circuitName: meeting.circuitName,
            detail,
            label: formatSeasonPointEventLabel({
              meetingNumber: meeting.meetingNumber,
              kind: 'qual_fewest_dice',
              detail,
              points: COURSE_CURRENCY_FEWEST_DICE_ALL_QUALS,
            }),
          });
        }
      }
    }
  }

  for (const sess of sessions) {
    if (sess.status !== 'completed') continue;
    for (const r of sess.results) {
      if (r.driverId !== driverId) continue;
      const stype = sess.sessionType as MeetingSessionType;

      const heatWin = scoreCourseCurrencyQualHeatWin(stype, r.position);
      if (heatWin > 0) {
        const detail = ordinalFr(r.position);
        events.push({
          kind: 'qual_heat_win',
          bucket: 'course',
          points: heatWin,
          meetingNumber: sess.meetingNumber,
          meetingId: sess.meetingId,
          sessionType: sess.sessionType,
          circuitName: sess.circuitName,
          position: r.position,
          detail,
          label: formatSeasonPointEventLabel({
            meetingNumber: sess.meetingNumber,
            kind: 'qual_heat_win',
            detail,
            points: heatWin,
            sessionType: sess.sessionType,
          }),
        });
      }

      const placePts = scoreCourseCurrencySessionPosition(stype, r.position);
      if (placePts > 0) {
        const detail = ordinalFr(r.position);
        events.push({
          kind: 'session_position',
          bucket: 'course',
          points: placePts,
          meetingNumber: sess.meetingNumber,
          meetingId: sess.meetingId,
          sessionType: sess.sessionType,
          circuitName: sess.circuitName,
          position: r.position,
          detail,
          label: formatSeasonPointEventLabel({
            meetingNumber: sess.meetingNumber,
            kind: 'session_position',
            detail,
            points: placePts,
            sessionType: sess.sessionType,
          }),
        });
      }

      if (!stype.startsWith('qual')) {
        const overtakeTotal = sessionResultOvertakeDuelsForScoring(r);
        const overtakePoints = scoreOvertakeDuels(overtakeTotal);
        if (overtakePoints > 0) {
          const detail = `${overtakeTotal} duel${overtakeTotal > 1 ? 's' : ''}`;
          events.push({
            kind: 'overtake_duels',
            bucket: 'course',
            points: overtakePoints,
            meetingNumber: sess.meetingNumber,
            meetingId: sess.meetingId,
            sessionType: sess.sessionType,
            circuitName: sess.circuitName,
            detail,
            label: formatSeasonPointEventLabel({
              meetingNumber: sess.meetingNumber,
              kind: 'overtake_duels',
              detail,
              points: overtakePoints,
              sessionType: sess.sessionType,
            }),
          });
        }
      }
    }
  }

  return events;
}

export function sumSeasonPointEvents(events: readonly SeasonPointEvent[]): number {
  return events.reduce((sum, e) => sum + e.points, 0);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildSeasonPointPositionBadgeHtml(position: number): string {
  if (!Number.isFinite(position) || position < 1) return '';
  const ordinal = ordinalFr(position);
  const medalClass =
    position === 1
      ? ' standings-points-tip__pos--gold'
      : position === 2
        ? ' standings-points-tip__pos--silver'
        : position === 3
          ? ' standings-points-tip__pos--bronze'
          : '';
  return `<span class="standings-points-tip__pos${medalClass}" aria-label="${escapeHtml(ordinal)}">${position}</span>`;
}

function tipBadgePairHtml(shortText: string, longText: string): string {
  return `<span class="standings-points-tip__short">${escapeHtml(shortText)}</span><span class="standings-points-tip__long">${escapeHtml(longText)}</span>`;
}

function tipMiddleHtml(event: SeasonPointEvent): string {
  const position = event.position ?? parseOrdinalFr(event.detail);
  if (
    (event.kind === 'session_position' ||
      event.kind === 'qual_championship' ||
      event.kind === 'qual_race_points') &&
    position != null &&
    event.kind !== 'qual_race_points'
  ) {
    return buildSeasonPointPositionBadgeHtml(position);
  }
  if (event.kind === 'session_position' && position != null) {
    return buildSeasonPointPositionBadgeHtml(position);
  }
  const body = formatSeasonPointTipBody(event);
  if (!body) {
    if (position != null && event.kind === 'qual_race_points') {
      return `<span class="standings-points-tip__body">${escapeHtml(event.detail)}</span>`;
    }
    return '';
  }
  return `<span class="standings-points-tip__body">${escapeHtml(body)}</span>`;
}

const BUCKET_HEAD: Record<SeasonPointBucket, string> = {
  championship: 'Points championnat',
  qualification: 'Points qualification',
  course: 'Points de course',
};

/** HTML compact pour tooltip (liste des gains, plus récent d’abord). */
export function buildSeasonPointsBreakdownTooltipHtml(
  breakdown: SeasonPointBreakdown,
  bucket: SeasonPointBucket = 'championship'
): string {
  const sorted = [...breakdown.events].sort(compareSeasonPointEventsRecentFirst);
  const rows =
    sorted.length === 0
      ? `<li class="standings-points-tip__empty">Aucun gain enregistré</li>`
      : sorted
          .map((e) => {
            const meetingShort = formatSeasonPointMeetingBadge(e.meetingNumber);
            const meetingLong = formatSeasonPointMeetingLabel(e.meetingNumber);
            const meetingTitle = escapeHtml(
              formatSeasonPointMeetingTitle(e.meetingNumber, e.circuitName)
            );
            const phase = formatSeasonPointPhaseBadge(e);
            const phaseHtml = phase
              ? `<span class="standings-points-tip__phase" title="${escapeHtml(phase.title)}">${tipBadgePairHtml(phase.code, phase.label)}</span>`
              : '';
            const middle = tipMiddleHtml(e);
            const rowTitle = escapeHtml(e.label);
            return `<li class="standings-points-tip__row" title="${rowTitle}">
              <span class="standings-points-tip__meeting" title="${meetingTitle}" aria-label="${meetingTitle}">${tipBadgePairHtml(meetingShort, meetingLong)}</span>
              ${phaseHtml}
              ${middle}
              <span class="standings-points-tip__pts">+${e.points}</span>
            </li>`;
          })
          .join('');
  return `<div class="standings-points-tip" role="tooltip">
    <div class="standings-points-tip__head">${BUCKET_HEAD[bucket]} · <strong>${breakdown.total}</strong></div>
    <ul class="standings-points-tip__list">${rows}</ul>
  </div>`;
}

/** Utilisé pour tests / exports secondaires. */
export { scoreQualRace };
